import React, { useState, useEffect } from 'react';
import axios from '../../../services/api';
import { FaTimes, FaEdit, FaTrash, FaSearch, FaTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CreateRoomModal from './CreateRoomModal';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import { useTranslation } from 'react-i18next';

const RoomModal = ({ isOpen,onSuccess, onClose, building }) => {
  const { t } = useTranslation();
  const [rooms, setRooms] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    if (isOpen && building) {
      fetchRooms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, building]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`/rooms/${building._id}`);
      setRooms(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Get unique floors from rooms list
  const floorOptions = [...new Set(rooms.map(room => room.floor))].sort((a, b) => a - b);

  // Get filtered rooms based on floor and search
  const filteredRooms = rooms
    .filter(room => !selectedFloor || room.floor.toString() === selectedFloor.toString())
    .filter(room =>
      room.roomNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      room.floor.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleDelete = async (room) => {
    try {
      await axios.delete(`/rooms/${room._id}`);
      toast.success(t('Deleted successfully'));
      fetchRooms();
      onSuccess();
    } catch (error) {
      console.error(error);
    }
  };
 // Add handlers
  const handleEdit = (room) => {
    setSelectedRoom(room);
    setIsCreateModalOpen(true);
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[10%] pt-[5%] z-50">
      <div className="bg-white w-[60vw] h-[70vh] rounded-lg shadow-xl">
        <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
          <h2 className="text-lg font-semibold">{t('Rooms')}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <FaTimes />
          </button>
        </div>

        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <select
                value={selectedFloor}
                onChange={(e) => setSelectedFloor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2"
                placeholder={t('Select Floor')}
              >
                <option value="">{t('All Floors')}</option>
                {floorOptions.map(floor => (
                  <option key={floor} value={floor}>{floor}</option>
                ))}
              </select>
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('Search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 pl-9 pr-8"
                />
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FaTimesCircle />
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              {t('Create New')}
            </button>
          </div>

          <div className={`${filteredRooms.length > 0 ? 'overflow-auto' : 'overflow-hidden'} h-[calc(70vh-150px)]`}>
            <table className="w-full">
              <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
                <tr className="border-b border-skin-base/10">
                  <th className="sticky left-0 z-10 bg-white px-4 py-2 font-semibold text-skin-base text-left">{t('No')}</th>
                  <th className="px-4 py-2 font-semibold text-skin-base text-left">{t('Floor')}</th>
                  <th className="px-4 py-2 font-semibold text-skin-base text-left">{t('Room No')}</th>
                  <th className="sticky right-0 z-10 bg-white px-4 py-2 font-semibold text-skin-base w-[100px] text-right">{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRooms.map((room, index) => (
                  <tr key={room._id}>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{index + 1}</td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{room.floor}</td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{room.roomNumber}</td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => handleEdit(room)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRoom(room);
                            setIsConfirmOpen(true);
                          }}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <CreateRoomModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedRoom(null);
        }}
        building={building}
        room={selectedRoom}
        onSuccess={() => {
          fetchRooms();
          onSuccess();
        }}
      />
      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setSelectedRoom(null);
        }}
        onConfirm={() => {
          handleDelete(selectedRoom);
          setIsConfirmOpen(false);
          setSelectedRoom(null);
        }}
        title={t('Delete Room')}
        message={t('Are you sure you want to delete this room? This action cannot be undone.')}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default RoomModal;
